<template>
  <div class="row justify-left full-width q-py-none no-wrap">
    <div class="col">
      <div style="position: relative;" class="row justify-left full-width q-py-none no-wrap">

        <q-btn
          class="q-my-none q-pa-none text-left text-md unset-items-center"
          flat no-caps align="left" size="xs" color="grey-8"
          icon="code" :aria-label="$t('choose_upcase')"
          v-if="!readOnly"
        >
          <q-tooltip :delay="1000" anchor="top middle" self="center middle">
            {{ $t('select_parameter') }}
          </q-tooltip>
          <q-menu fit style="overflow: hidden;"
            v-model="selectorOpen" @before-show="(e: any) => { filter_string = '' }"
          >
            <q-input outlined dense ref="panel_search_input" color="white" class="q-px-sm full-width thin-input q-my-sm" 
              v-model="filter_string" debounce="100" :placeholder="$t('search_label')" autofocus
              @keydown.down.exact.prevent="first_selector && (first_selector as any).trackArrowDown(1)"
              @keydown.up.exact.prevent="first_selector && (first_selector as any).trackArrowUp(1)"
              @keyup.enter="first_selector && (first_selector as any).selectCurrent()" />
            <div style="max-height: 50vh; overflow: auto; border-top: 1px solid lightgrey; padding-top: 5px;" class="q-px-sm"
            >
                <div>
                  {{ !globalFormulasMode ? $t('parameters') : $t('formulas') }}
                </div>
                <div v-if="inputParameterOptions && inputParameterOptions.length">
                  <recursive-selector :style="globalFormulasMode && op_key === 0 ? 'border-bottom: 1px solid black; padding-bottom: 5px;' : ''"
                  :ref="(el: any) => { divs[op_key] = el }"
                  v-for="(opts, op_key) in inputParameterOptions" :key="op_key"
                  :elements="opts" :take-ids-only="true" :hide-checkboxes="true"
                  :allow-multiple="false" :show-records="true" :element-type="9" v-model="selectedParameterModel" 
                  return-type="chain" :nested-key-name="nestedKeyName" :entity-inside-name="templateName"
                  :filter-string="filter_string" />
                </div>
                <recursive-selector ref="rec_selector"
                  v-else-if="columnParameterOptions && columnParameterOptions.length"
                  :elements="columnParameterOptions" :take-ids-only="true" :hide-checkboxes="true"
                  :allow-multiple="false" :element-type="1" v-model="selectedColumnParameterModel"
                  :is-column="true" :filter-string="filter_string" />
                <div v-if="!globalFormulasMode" class="q-mt-sm">
                  {{ $t('formulas') }}
                </div>
                <recursive-selector ref="rec_selector"
                  v-if="formulaOptions && formulaOptions.length"
                  :elements="formulaOptions" :take-ids-only="true" :hide-checkboxes="true"
                  :allow-multiple="false" :element-type="1" v-model="selectedFormulaModel"
                  :filter-string="filter_string" />
              </div>
          </q-menu>
        </q-btn>
        <!-- :hide-bottom-space="parameterOptions ? true : false" -->
        <!-- class="formula-input" -->
        <q-select
          v-if="!readOnly"
          v-model="innerFormulaModel"
          ref="formula_field"
          class="formula-input"
          outlined
          dense
          autogrow
          use-input
          hide-bottom-space
          multiple
          no-error-icon
          input-debounce="500"
          :placeholder="displayFormulaStr ? '' : $t('empty_at_all')"
          :error-message="isIncorrect"
          :error="isIncorrect ? true : false"
          :display-value="displayFormulaStr"
          :display-value-html="true"
          :use-chips="false"
          :hide-dropdown-icon="true"
          :options="combinedOptions"
          @filter="filterInput"
          @add="addFormulaFunc"
          @new-value="addFormulaToken"
          @keydown.esc.stop.prevent="() => {}"
          @keyup.esc.stop.prevent="resetFormula"
          @blur="onBlur"
        />
        <!-- @mousedown.prevent -->
        <div v-else v-html="displayFormulaStr" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue, Watch } from 'vue-facing-decorator';
import { TokenTypes, Tokenizer, detailTokenType, findParametersByChain, makeApiFormulaToken, 
  makeHtmlTokenTemplate } from '@/utils/formulas';
import { settingsStore } from '@/store/settings';
import { ColumnSend } from '@/models/interfaces/entity-settings/columns/columns';
import { ColumnGroupItem, ColumnListItem } from '@/components/common/base-list/interfaces/ListTypes';
import { reactive, readonly } from 'vue';
import { CommonEntityRecord } from '@/models/interfaces/common';
import { arrayEquals, findRecursive, foreachRecursive } from '@/models/helpers/recursive';
import { AnalyticsFormulaLinkToParameter, AnalyticsFormulaOperand, AnalyticsFormulaOperation, AnalyticsFormulaToken, ParameterLinkSummable, ParameterLinkSummablePart } from '@/models/interfaces/entity-settings/parameters/parameters';
import RecursiveSelector from '@/components/common/basic/recursive-selector/refactored/RecursiveSelector.vue';
import { AnalyticsFormulaTokenDescriptor, AnalyticsFormulaOperationType } from '@/models/etc/types';
import _ from 'lodash';

// for matching string values that are in a wrong keyboard layout
const ruLayout = require('convert-layout/ru');

@Component({
  name: 'FormulaSelector',
  components: { RecursiveSelector }
})
export default class FormulaSelector extends Vue {
  // v-model
  @Prop({ default: [] })
  modelValue: any | undefined
  @Emit("update:modelValue") emitModelValue(arg: any[] | undefined ) { return arg; }
  @Emit("theOnlyFormulaLeft") emitSingleNameValue(arg: string) { return arg; }

  // Input options for parameter selector (ParameterSummableLink[])
  @Prop({ default: undefined })
  inputParameterOptions?: any[][]
  @Prop({ default: false })
  readOnly?: boolean
  // Input options for another formulas list (LinkToFormula)
  @Prop({ default: [] })
  formulaOptions?: any[]
  @Prop({ default: 0 })
  inputIndex?: number
  tempValue: string = ''
  // Both parameterModel and formulaModel point to TokenTypes.NOTNUMBER but eventually 
  // make different API types: ParameterSummableLink[] or LinkToFormula

  containsErrors: boolean = false
  selectorOpen: boolean = false
  // if > 0 then it means we are in replace mode and should assign this value at the replaceIndex on innerFormulaModel;
  // should be set to -1 again
  replaceIndex: number = -1
  filter_string: string = ''
  setstore: any | undefined = undefined

  first_selector: any | undefined = undefined
  // dynamic item refs
  divs: any[] = reactive([])

  // combined [ columnModel and formulaModel ] or [ parameterModel and formulaModel ] 
  // options list for filtering on input
  @Prop({ default: '' })
  nestedKeyName!: string
  @Prop({ default: '' })
  templateName!: string
  // means that parameter-options are actually links to global formulas
  @Prop({ default: false })
  globalFormulasMode!: boolean
  combinedOptions: any[] = reactive([])
  // Transform recursive params to linear to linear function
  parameterOptionsToList: any[] = reactive([])

  // Selected options from parameterOptions, formulaOptions and columnParameterOptions
  // Each ParameterSummableLink[] part from the parameterModel should have its own formulaToken and
  // Summable property
  parameterModel: any = reactive({ IdChain: [] })
  formulaModel: any = reactive([])
  columnModel: any = reactive([])
  innerFormulaModel: any[] = reactive([])

  // did not pass validation
  @Prop({ default: '' })
  isIncorrect!: string

  @Watch('filter_string')
  // change focus from search field to list when up and down arrow keys are pressed
  focusPanel() {
    const that = this
    setTimeout(() => {
      if (that.inputParameterOptions && that.inputParameterOptions.length) {
        // use divs
      } else {
        that.first_selector && (that.first_selector as any).trackArrowDown(1, true)
      }
    }, 100)
  }

  @Watch('selectorOpen')
  replace_reset_watcher() {
    !this.selectorOpen && (this.replaceIndex = -1)
  }

  columnToParameter(val: ColumnListItem[]) {
    return val.map((el: ColumnListItem) => {
      return {
        Id: el.Entity?.Id,
        Name: el.Entity ? el.Entity.Name : el.compound_name,
        IsList: false // always summable despite this.
      }
    })
  }
  
  get parameterOptions(): any[] | undefined {
    return this.inputParameterOptions ? this.inputParameterOptions.flat(1) : undefined
  }

  get displayFormulaStr(): string {
    return this.innerFormulaModel ? this.innerFormulaModel.map((el: any) => el.label).join(' ') : ''
  }

  onBlur() {
    console.log('blur')
    console.log(this.tempValue)
    if (!this.$refs.formula_field) { return }
    console.log('blur 1')
    console.log(this.tempValue)
    // @ts-ignore
    this.addFormulaToken(this.tempValue, this.$refs.formula_field.add)
  }

  created() {
    this.setstore = settingsStore()
    this.parameterOptionsToList = reactive([])
  }

  @Watch('parameterOptions', { deep: true })
  parameters_loading_detector(arg: any, old_arg: any) {
    console.log(arg)
    console.log(old_arg)
    if (arg && arg.length && (!old_arg || !old_arg.length)) {
      const that = this
      this.$nextTick(() => {
        setTimeout(() => {
          that.updateTokenValues();
          that.actualModelUpdate();
        }, 10)
      })
    }
  }

  updateTokenValues() {
    if (this.parameterOptions) {
      const that = this
      foreachRecursive(function a(element: any, el_to_find: any): any | undefined {
        if (!element) return

        const new_name = (el_to_find.name ? el_to_find.name+'.' : '')+
          (that.templateName ? element[that.templateName].Name : element.Name)
        const new_chain = [ ...el_to_find.chain, that.templateName ? element[that.templateName].Id : element.Id ]
        // innerFormulaModel element
        if ([2, 5].includes(element.Descriptor) || (that.globalFormulasMode && (!element.ChildFormulas || !element.ChildFormulas.length))) {
          that.parameterOptionsToList.push({
            ...element,
            IdChain: new_chain,
            compound_name: new_name
          })
        }
        
        if (element[that.nestedKeyName]) {
          foreachRecursive(a, {
            el_to_find: el_to_find.el_to_find ? el_to_find.el_to_find : element, 
            name: new_name,
            chain: new_chain
          }, element[that.nestedKeyName])
        }
        return
      }, { el_to_find: undefined, IdChain: '', chain: [] }, this.parameterOptions)
      // return this.parameterOptionsToList

      // TODO: add GlobalFormulas to combined options
    }
  }

  mounted() {
    if (this.inputParameterOptions && this.inputParameterOptions.length) {
      this.first_selector = this.divs[0]
    } else {
      this.first_selector = this.$refs.rec_selector
    }
    this.updateTokenValues();
    this.actualModelUpdate();
  }

  // common formula model
  @Watch('modelValue')
  updateInnerModelWatcher(arg: any, old_arg: any) {
    if (!this.modelValue || arrayEquals(arg, this.innerFormulaModel.map((el: any, index: number) => el.apiToken ))) {
      return
    }
    this.actualModelUpdate()
  }

  actualModelUpdate() {
    console.log('actual update')
    console.log(this.modelValue.length)

    this.innerFormulaModel = this.modelValue.map((el: any, index: number) => {
      const getTextFromToken = function(a_token: AnalyticsFormulaToken) {
        switch (a_token.Descriptor) {
          case AnalyticsFormulaTokenDescriptor.LeftBracket:
            return '(';
            case AnalyticsFormulaTokenDescriptor.RightBracket:
            return ')';
            case AnalyticsFormulaTokenDescriptor.TotalResultsCount:
            return '?';
            case AnalyticsFormulaTokenDescriptor.Operand:
            return ''+(a_token as AnalyticsFormulaOperand).Value;
            case AnalyticsFormulaTokenDescriptor.Operation:
              switch ((a_token as AnalyticsFormulaOperation).OperationType) {
                case AnalyticsFormulaOperationType.Divide:
                  return '/';
                case AnalyticsFormulaOperationType.Minus:
                  return '-';
                case AnalyticsFormulaOperationType.Multiply:
                  return '*';
                case AnalyticsFormulaOperationType.Plus:
                default:
                  return '+';
              }
            default:
              return '';
        }
      }

      let found_element = undefined;
      if (el && (el.ParameterLink || this.globalFormulasMode)) {
        // find element by link parts
        if (this.parameterOptions) {
          if (this.globalFormulasMode) {
            const that = this
            found_element = findRecursive(function a(element: any, el_to_find: any): any | undefined {
              if (!element) return undefined

              if ((!that.templateName || element[that.templateName]) && 
              el_to_find.OwnerId === element.Identity?.ElementId &&
              el_to_find.FormulaId === (that.templateName ? element[that.templateName].PureId : element.Id) &&
              (!el_to_find.OwnerSource || (el_to_find.OwnerSource === element.Identity?.Source)) &&
              el_to_find.OwnerKind === element.Identity?.Kind) {
                return [element];
              }
              if (element[that.nestedKeyName]) {
                const childparam = findRecursive(a, el_to_find, element[that.nestedKeyName])
                return childparam ? [ element, ...childparam ] : undefined
              }
              return undefined
            }, el, this.parameterOptions as any[])
          } else {
            found_element = findParametersByChain((el.ParameterLink as ParameterLinkSummable).
              LinkParts.map((elm: ParameterLinkSummablePart) => elm.Id), 
              this.parameterOptions as any[], this.nestedKeyName, this.templateName)
          }
        } else {
          found_element = findParametersByChain((el.ParameterLink as ParameterLinkSummable).
            LinkParts.map((elm: ParameterLinkSummablePart) => elm.Id), 
          this.setstore.getAllColumns, 'ChildColumns', 'Entity')
        }
      } else if (el && el.FormulaId && this.formulaOptions) {
        found_element = this.formulaOptions.find((form: any) => form.Id === el.FormulaId)
      }

      let return_el = undefined
      if (found_element) {
        return_el = {
          label: '',
          value: index,
          innerData: (el.ParameterLink || this.globalFormulasMode) ? (this.parameterOptions ? 
            // parameters
            found_element.map((el: any) => this.templateName ? el[this.templateName].Name : el.Name).join('.') :
            // columns as parameters
            found_element.map((el: any) => el.Entity ? el.Entity.Name : el.compound_name).join('.')) :
            found_element.Name,
          apiToken: el,
          ref_element_id: 'token_'+this.inputIndex+'_'+index,
          replace: true,
          element: (el.ParameterLink || this.globalFormulasMode) ? 
          (this.parameterOptions ? found_element : this.columnToParameter(found_element)) :
          found_element
        }
      } else {
        return_el = {
          label: '',
          value: index,
          innerData: el ? getTextFromToken(el) : this.$t('variable'),
          apiToken: el,
          ref_element_id: 'token_'+this.inputIndex+'_'+index,
          replace: true,
          element: undefined
        }
      }

      // return_el.label = makeHtmlTokenTemplate({
      //   // element property only exists temporarily when the user 
      //   // selects something from a compound list
      //   textValue: return_el.innerData,
      //   originElement: return_el.element
      // }, return_el.apiToken, 'token_'+this.inputIndex+'_'+index, this, this.toggleSum, this.toggleReplaceElement, this.templateName, true, this.readOnly)

      return return_el
    })
    // replace tokens
    console.log('updated; this.innerFormulaModel.length: '+this.innerFormulaModel.length)
    this.addFormulaFunc('')
  }

  // set innerFormulaModel(val: any[] | undefined) {
  //   // this.emitModelValue(val)
  //   val && this.emitModelValue(val.map((el: any, index: number) => el.apiToken ))
  // }

  // parameters model for recursive selector
  get selectedParameterModel(): any {
    return [this.parameterModel.IdChain]
  }

  // recursive parameter chain as elements array corresponding to IdChain. 
  // Each part should contain several tokens and several click listeners
  // for changing summable/first element prop

  get selectedParameterElement(): any[] {
    if (!this.parameterOptions || !this.parameterModel || !this.parameterModel.IdChain) { return [] }
    return findParametersByChain(this.parameterModel.IdChain, this.parameterOptions, this.nestedKeyName, this.templateName)
  }

  set selectedParameterModel(val: any) {
    if (this.innerFormulaModel) {
      this.parameterModel = { IdChain: val[0] }
    }
  }

  // formulaModel for recursive selector
  get selectedFormulaModel(): any {
    if (!this.formulaModel || !this.formulaOptions) { return [] }
    return this.formulaOptions.filter((el: any) => el.Id === this.formulaModel[0])
  }

  set selectedFormulaModel(val: any) {
    if (this.innerFormulaModel) {
      this.formulaModel = val
    }
  }

  // column parameters model for recursive selector
  get selectedColumnParameterModel() {
    if (!this.columnModel || !this.columnParameterOptions) { return [] }
    return this.columnParameterOptions.filter((el: any) => {
      return arrayEquals(el.IdChain, this.columnModel[0])
    })
  }

  set selectedColumnParameterModel(val: any) {
    if (this.innerFormulaModel) {
      this.columnModel = val
    }
  }

  get selectedColumnParameterElement(): any[] {
    if (!this.columnParameterOptions || !this.columnModel) { return [] }
    return findParametersByChain(this.columnModel[0], this.setstore.getAllColumns, 'ChildColumns', 'Entity')
  }

  get columnParameterOptions() {
    if (!this.setstore) { return [] }
    const res_arr: any[] = reactive([])
    if (this.setstore && this.setstore.getSelectedColumns) {
      const arr = this.setstore.getSelectedColumns

      // console.log('selected columns')
      // console.log(arr)
      for (let c = 0; c < arr.length; c++) {
        if (!arr[c].sub_items || !arr[c].sub_items.length) {
          arr[c].UsableInTableFormulas && res_arr.push(arr[c])
        } else {
          for (let cin = 0; cin < arr[c].sub_items.length; cin++) {
            if (arr[c].sub_items[cin].UsableInTableFormulas) {
              res_arr.push(arr[c].sub_items[cin])
            }
          }
        }
      }
    }
    return res_arr
  }

  // helper functions
  // this function is called on Enter, so exact match is needed
  lookUpInColumns(val: string): any {
    if (this.columnParameterOptions) { 
      const element = this.columnParameterOptions.find((el: ColumnSend) => {
        return (el as ColumnGroupItem).compound_name.toLowerCase() === val.toLowerCase() ||
        ruLayout.toEn((el as ColumnGroupItem).compound_name).toLowerCase() === val.toLowerCase()
      })
      if (element) {
        return findParametersByChain(element.IdChain, this.setstore.getAllColumns, 'ChildColumns', 'Entity')
      }
    }
    return undefined
  }

  // this function is called on Enter, so exact match is needed
  lookUpInParameters(val: string): any {
    if (this.parameterOptions) {

      const that = this
      const params = findRecursive(function a(element: any, el_to_find: any): any | undefined {
        if (!element) return undefined

        const jointname = (el_to_find.compname ? (el_to_find.compname + '.') : '')+(that.templateName ? element[that.templateName].Name : element.Name)
        if ((!that.templateName || element[that.templateName]) && 
          (jointname.toLowerCase() === el_to_find.el_to_find)) {
          return [element];
        }
        if (element[that.nestedKeyName]) {
          const childparam = findRecursive(a, {
            chain: (!that.templateName || element[that.templateName]) ? 
              [ ...el_to_find.chain, that.templateName ? element[that.templateName].Id : element.Id ] : 
              el_to_find.chain,
            compname: (!that.templateName || element[that.templateName]) ? 
              (el_to_find.compname ? (el_to_find.compname + '.') : '') + (that.templateName ? element[that.templateName].Name : element.Name) : 
              el_to_find.compname,
            el_to_find: el_to_find.el_to_find
          }, element[that.nestedKeyName])
        
          return childparam ? [ element, ...childparam ] : undefined
        }
        return undefined
      }, { chain: [], compname: '', el_to_find: val.trim().toLowerCase() }, this.parameterOptions)

      return params
    }
    return undefined
  }

  // this function is called on Enter, so exact match is needed
  lookUpInFormulas(val: string): any {
    if (this.formulaOptions) { 
      return this.formulaOptions.find((el: CommonEntityRecord) => {
        return el.Name.toLowerCase() === val.toLowerCase() ||
        ruLayout.toEn(el.Name).toLowerCase() === val.toLowerCase()
      })
    }
    return undefined
  }

  // This happens when the user presses the Enter key on some random input. So validation is needed
  addFormulaToken(val: any, done: any, index: number = -1) {
    if (!this.innerFormulaModel) { return }
    // specific logic to eventually call done(...) -- or not
    const tokenizer = new Tokenizer(""+val);

    let token: any;
    const all_tokens: any[] = []

    try {
      while ((token = tokenizer.getNextToken(!all_tokens.length || 
      (all_tokens[all_tokens.length-1].type == TokenTypes.ADDITION ||
      all_tokens[all_tokens.length-1].type == TokenTypes.SUBTRACTION ||
      all_tokens[all_tokens.length-1].type == TokenTypes.DIVISION ||
      all_tokens[all_tokens.length-1].type == TokenTypes.MULTIPLICATION ||
      all_tokens[all_tokens.length-1].type == TokenTypes.PARENTHESIS_LEFT)))) {
        all_tokens.push(token)
      }
    } catch(err: any) {
      console.log(err)
      if (!this.$refs.formula_field) { return }
      // @ts-ignore
      this.$refs.formula_field.updateInputValue('', true)
      return
    }

    // erroneous tokens are red
    // operation tokens and brackets are grey
    // numbers are blue
    // selected value tokens are green
    const max_len = this.innerFormulaModel.length
    for (let t = 0; t < all_tokens.length; t++) {
      // if this token is NOTNUBMER then look up through the parameters, selectedFilters and 
      // formula values (depending on what is available) in order to find the one with the same name
      let found_out_type: detailTokenType = detailTokenType.Unknown;
      let new_token = undefined
      let foundParam = undefined
      if (all_tokens[t].type === TokenTypes.NOTNUMBER) {

        if (!this.parameterOptions) 
        {
          const columnParam = this.lookUpInColumns(""+all_tokens[t].value)
          if (columnParam) {
            found_out_type = detailTokenType.ColumnParameter
            new_token = makeApiFormulaToken(all_tokens[t], found_out_type, this.columnToParameter(columnParam))
            foundParam = this.columnToParameter(columnParam)
          }
        } else {
          // plain parameters options
          const plainParam = this.lookUpInParameters(""+all_tokens[t].value)
          if (plainParam) {
            found_out_type = this.globalFormulasMode ? detailTokenType.GlobalFormula : detailTokenType.CommonParameter
            new_token = makeApiFormulaToken(all_tokens[t], found_out_type, plainParam, this.templateName)
            foundParam = plainParam
          }
        }
        // check for formula here if found_out_type is still equals detailTokenType.Unknown
        if (found_out_type === detailTokenType.Unknown && this.formulaOptions) {
          const formulaParam = this.lookUpInFormulas(""+all_tokens[t].value)
          if (formulaParam) {
            found_out_type = detailTokenType.Formula
            new_token = makeApiFormulaToken(all_tokens[t], found_out_type, formulaParam)
            foundParam = formulaParam
          }
        }
      } else {
        new_token = makeApiFormulaToken(all_tokens[t], found_out_type)
      }

      let new_model_element
      // if erroneous then add listener for re-selecting a parameter/selectedFilter/formula
      if (!new_token) {
        console.log('1')
        console.log(new_token)

        new_model_element = {
          // TODO: maybe add a tooltip here saying that this is an error
          label: makeHtmlTokenTemplate({
            textValue: ""+all_tokens[t].value,
            originElement: undefined
          }, undefined, 'token_'+this.inputIndex+'_'+(max_len+t), this, ()=>{}, ()=>{}, ()=>{}, ()=>{}, max_len+t, this.templateName, true, this.readOnly),
          value: max_len + t,
          // what is displayed in "raw" mode, when input is active
          innerData: ""+all_tokens[t].value,
          apiToken: undefined,
          ref_element_id: 'token_'+this.inputIndex+'_'+(max_len+t),
          // indicates that this value should be replaced
          replace: true,
          element: undefined
        }
      } else {
        console.log('2')
        console.log(new_token)

        new_model_element = {
          label: makeHtmlTokenTemplate({
            textValue: ""+all_tokens[t].value,
            originElement: foundParam
          }, new_token, 'token_'+this.inputIndex+'_'+(max_len+t), this, 
          this.toggleSum, this.toggleReplaceElement, 
          this.toggleInsertElement, this.toggleRemovePrevious, max_len+t, this.templateName, true, this.readOnly),
          value: max_len + t,
          // what is displayed in "raw" mode, when input is active
          innerData: ""+all_tokens[t].value,
          apiToken: new_token,
          ref_element_id: 'token_'+this.inputIndex+'_'+(max_len+t),
          // indicates that this value should not be replaced
          // replace: false,
          replace: true,
          element: foundParam // IsList inside
        }
      }
    
      const new_i: number = Number(t)+Number(index)+Number(1)
      if (index < 0 || new_i > this.innerFormulaModel.length-1) {
        if (t < all_tokens.length-1) {
          // all tokens except the last one are added directly to the model
          this.innerFormulaModel.push(new_model_element)
        } else {
          // last token is added with the standard done() quasar selector api method
          // so that the input is finished approprietly
          done(new_model_element)
        }
      } else {
        this.innerFormulaModel.splice(new_i, 0, new_model_element);
        this.addFormulaFunc('')
      }
    }
    
    
    // done callback has two optional parameters:
    //  - the value to be added
    //  - the behavior (same values of new-value-mode prop,
    //    and when it is specified it overrides that prop –
    //    if it is used); default behavior (if not using
    //    new-value-mode) is to add the value even if it would
    //    be a duplicate
  }

  // This happens when the user selects an option from the compoundOptions selector (both parameters and formulas)
  // Those options don't have proper html and they have the 'replace' option set to true in order to refresh innerFormulaModel
  // So take all the necessary data here from the apiTokens
  addFormulaFunc(params: any) {
    this.tempValue = '';
    
    setTimeout(() => {
      this.$nextTick(() => {
        if (!this.innerFormulaModel || !this.innerFormulaModel.length) { return }

        console.log('update innerFormulaModel')
        this.containsErrors = false;
        this.innerFormulaModel = [ ...this.innerFormulaModel.map((el: any, index: number) => {
          if (el.replace) {

            console.log('2.1')
            console.log(el)

            if (!el.innerData) {
              this.containsErrors = true;
            }

            return {
              label: makeHtmlTokenTemplate({
                // element property only exists temporarily when the user 
                // selects something from a compound list
                textValue: /*el.label ? el.label : */el.innerData || 'ошибка',
                originElement: el.element
              }, el.innerData ? el.apiToken : undefined, 'token_'+this.inputIndex+'_'+index, this, this.toggleSum, 
              this.toggleReplaceElement, this.toggleInsertElement, this.toggleRemovePrevious,
               index, this.templateName, /*index === this.innerFormulaModel.length-1*/true, this.readOnly),
              value: index,
              innerData: el.innerData,
              apiToken: el.apiToken,
              ref_element_id: 'token_'+this.inputIndex+'_'+index,
              replace: true,
              element: el.element
            }
          } else {
            return el
          }
        }) ]
        if (!this.$refs.formula_field) { return }
        // @ts-ignore
        this.$refs.formula_field.updateInputValue('', true)
        // @ts-ignore
        this.$refs.formula_field.validate(this.innerFormulaModel)
        // @ts-ignore
        this.$refs.formula_field.hidePopup();
      })
    }, 10)
  }

  get formulaSettingsMutated() { return _.cloneDeep(this.innerFormulaModel) }

  // special check function that skips 'label' comparison
  modelEquals(a: any[], b: any[]): boolean {
    return Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((val, index) => {
        return _.isEqual(val.apiToken, b[index].apiToken) &&
        _.isEqual(val.element, b[index].element) &&
        val.innerData == b[index].innerData &&
        val.ref_element_id == b[index].ref_element_id &&
        val.value == b[index].value
      });
  }

  @Watch('formulaSettingsMutated', { deep: true })
  modelUpdated(arg: any[], old_arg: any[]) {
    if (this.modelEquals(arg, old_arg)) { return }

    // emit output model
    if (!this.containsErrors) {
      this.emitModelValue(this.innerFormulaModel.map((el: any, index: number) => el.apiToken ))
    }

    if (this.globalFormulasMode && (!old_arg || !old_arg.length) && this.innerFormulaModel && this.innerFormulaModel.length === 1) {
      const narr = this.innerFormulaModel[this.innerFormulaModel.length-1].innerData.split('.')
      // console.log('emit '+narr[narr.length-1])
      this.emitSingleNameValue(narr[narr.length-1]);
    }
  }

  // This happens when the user selects some parameter directly from the parameters recursive selector
  @Watch('selectedParameterModel', { deep: true })
  parameterWatcher() {
    if (!this.innerFormulaModel || !this.selectedParameterModel || !this.selectedParameterModel.length) { return }
    this.tempValue = '';
    
    const foundParam = this.selectedParameterElement
    if (!foundParam || !foundParam.length) { return }

    const foundParamName = foundParam.map((el: any) => this.templateName ? el[this.templateName].Name : el.Name).join('.')
    const new_token = makeApiFormulaToken({
      type: TokenTypes.NOTNUMBER,
      value: foundParamName
    }, this.globalFormulasMode ? detailTokenType.GlobalFormula : detailTokenType.CommonParameter, foundParam, this.templateName)

    console.log('4')
    console.log(foundParam)

    const new_model_element = {
      label: makeHtmlTokenTemplate({
        textValue: '',
        originElement: foundParam
      }, new_token, 'token_'+this.inputIndex+'_'+this.innerFormulaModel.length, this, 
      this.toggleSum, this.toggleReplaceElement, this.toggleInsertElement, 
      this.toggleRemovePrevious, this.innerFormulaModel.length, this.templateName, true, this.readOnly),
      value: this.innerFormulaModel.length,
      innerData: foundParamName,
      apiToken: new_token,
      ref_element_id: 'token_'+this.inputIndex+'_'+this.innerFormulaModel.length,
      // indicates that this value should not be replaced
      // replace: false
      replace: true,
      element: foundParam // IsList inside
    }

    if (this.replaceIndex < 0) {
      this.innerFormulaModel.push(new_model_element)
    } else {
      this.innerFormulaModel[this.replaceIndex] = new_model_element
      // this.innerFormulaModel = [ ...this.innerFormulaModel ]
      this.addFormulaFunc('')
    }

    // reset after addition
    this.selectorOpen = false
    this.selectedParameterModel = { IdChain: [] }
  }

  // This happens when the user selects some parameter directly from the parameters recursive selector
  @Watch('selectedColumnParameterModel', { deep: true })
  columnParameterWatcher() {
    if (!this.innerFormulaModel || !this.selectedColumnParameterModel || !this.selectedColumnParameterModel.length) { return }
    this.tempValue = '';
    
    const foundParam = this.selectedColumnParameterElement
    if (!foundParam || !foundParam.length) { return }

    const foundParamName = foundParam.map((el: any) => el.Entity ? el.Entity.Name : el.compound_name).join('.')
    // console.log(foundParamName)
    const new_token = makeApiFormulaToken({
      type: TokenTypes.NOTNUMBER,
      value: foundParamName
    }, detailTokenType.ColumnParameter, this.columnToParameter(foundParam))

    console.log('5')
    console.log(this.columnToParameter(foundParam))

    const new_model_element = {
      label: makeHtmlTokenTemplate({
        textValue: '',
        originElement: this.columnToParameter(foundParam)
      }, new_token, 'token_'+this.inputIndex+'_'+this.innerFormulaModel.length, 
      this, this.toggleSum, this.toggleReplaceElement,
      this.toggleInsertElement, this.toggleRemovePrevious, 
      this.innerFormulaModel.length, this.templateName, true, this.readOnly),
      value: this.innerFormulaModel.length,
      innerData: foundParamName,
      apiToken: new_token,
      ref_element_id: 'token_'+this.inputIndex+'_'+this.innerFormulaModel.length,
      // indicates that this value should not be replaced
      // replace: false
      replace: true,
      element: this.columnToParameter(foundParam) // IsList inside
    }

    if (this.replaceIndex < 0) {
      this.innerFormulaModel.push(new_model_element)
    } else {
      this.innerFormulaModel[this.replaceIndex] = new_model_element
      // this.innerFormulaModel = [ ...this.innerFormulaModel ]
      this.addFormulaFunc('')
    }

    // reset after addition
    this.selectorOpen = false
    this.selectedColumnParameterModel = []
  }

  // This happens when the user selects some formula directly from the formulas recursive selector
  @Watch('selectedFormulaModel', { deep: true })
  formulaWatcher() {
    if (!this.innerFormulaModel || !this.selectedFormulaModel || !this.selectedFormulaModel.length) { return }
    this.tempValue = '';

    const foundFormula = this.selectedFormulaModel[0]

    console.log('6')
    console.log(foundFormula)
    
    const new_token = makeApiFormulaToken({
      type: TokenTypes.NOTNUMBER,
      value: foundFormula.Name
    }, detailTokenType.Formula, foundFormula)
    const new_model_element = {
      label: makeHtmlTokenTemplate({
        textValue: foundFormula.Name,
        originElement: foundFormula
      }, new_token, 'token_'+this.inputIndex+'_'+this.innerFormulaModel.length, 
      this, ()=>{}, ()=>{}, ()=>{}, ()=>{},
      this.innerFormulaModel.length, this.templateName, true, this.readOnly),
      value: this.innerFormulaModel.length,
      innerData: foundFormula.Name,
      apiToken: new_token,
      ref_element_id: 'token_'+this.inputIndex+'_'+this.innerFormulaModel.length,
      // indicates that this value should not be replaced
      // replace: false
      replace: true,
      element: foundFormula
    }

    if (this.replaceIndex < 0) {
      this.innerFormulaModel.push(new_model_element)
    } else {
      this.innerFormulaModel[this.replaceIndex] = new_model_element
      // this.innerFormulaModel = [ ...this.innerFormulaModel ]
      this.addFormulaFunc('')
    }

    // reset after addition
    this.selectorOpen = false
    this.selectedFormulaModel = []
  }

  // this happens when the user inputs something.
  // NOTNUMBER-typed values automatically filter the compoundOptions selector
  filterInput(val: any, update: any, abort: Function) {
    this.tempValue = val
    // parse last token firstly
    // specific logic to eventually call done(...) -- or not
    const tokenizer = new Tokenizer(""+val);

    let token: any;
    const all_tokens: any[] = []

    try {
      while ((token = tokenizer.getNextToken(!all_tokens.length || 
      (all_tokens[all_tokens.length-1].type == TokenTypes.ADDITION ||
      all_tokens[all_tokens.length-1].type == TokenTypes.SUBTRACTION ||
      all_tokens[all_tokens.length-1].type == TokenTypes.DIVISION ||
      all_tokens[all_tokens.length-1].type == TokenTypes.MULTIPLICATION ||
      all_tokens[all_tokens.length-1].type == TokenTypes.PARENTHESIS_LEFT)))) {
        all_tokens.push(token)
      }
    } catch(err: any) {
      console.log(err)
      if (!this.$refs.formula_field) { return }
      // @ts-ignore
      this.$refs.formula_field.updateInputValue('', true)
      return
    }

    // add options
    update(() => {
      this.combinedOptions.splice(0)
      if (!all_tokens[all_tokens.length-1] || this.readOnly) { return }

      if (all_tokens[all_tokens.length-1].type === TokenTypes.ADDITION ||
      all_tokens[all_tokens.length-1].type === TokenTypes.DIVISION ||
      all_tokens[all_tokens.length-1].type === TokenTypes.MULTIPLICATION ||
      all_tokens[all_tokens.length-1].type === TokenTypes.TOTALSUM ||
      all_tokens[all_tokens.length-1].type === TokenTypes.SUBTRACTION ||
      all_tokens[all_tokens.length-1].type === TokenTypes.PARENTHESIS_LEFT ||
      all_tokens[all_tokens.length-1].type === TokenTypes.PARENTHESIS_RIGHT) {
        if (!this.$refs.formula_field) { return }
        // console.log('add '+val)
        // @ts-ignore
        this.addFormulaToken(val, this.$refs.formula_field.add)
        // @ts-ignore
        this.$refs.formula_field.updateInputValue('', true)
        this.tempValue = ''
        return
      }

      // even if it is a number look for parameters/formulas anyway
      all_tokens[all_tokens.length-1].type = TokenTypes.NOTNUMBER;
      if (!this.parameterOptions) {
        // if no parameter options then look up in selected columns
        // console.log('ddd')
        // console.log(this.columnParameterOptions)
        // console.log(all_tokens[all_tokens.length-1])
        this.combinedOptions = (all_tokens[all_tokens.length-1] ? 
          this.columnParameterOptions.filter((el: ColumnSend) => { return ((el as ColumnGroupItem).compound_name.toLowerCase().includes(all_tokens[all_tokens.length-1].value.toLowerCase()) ||
            ruLayout.toEn((el as ColumnGroupItem).compound_name).toLowerCase().includes(all_tokens[all_tokens.length-1].value.toLowerCase()))})
          : [])
        .map((el: ColumnGroupItem, t: number) => {
          const foundParam = findParametersByChain(el.IdChain, this.setstore.getAllColumns, 'ChildColumns', 'Entity')
          return {
            label: el.compound_name,
            // these values should not intersect with innerFormulaModel values so that the same element could be added many times
            value: 'param_'+t,
            innerData: ""+el.compound_name,
            apiToken: makeApiFormulaToken(all_tokens[all_tokens.length-1], detailTokenType.ColumnParameter, this.columnToParameter(foundParam)),
            // indicates that this value should be replaced
            replace: true,
            element: this.columnToParameter(foundParam) // IsList inside
          }
        })
      } else if (this.parameterOptions) {
        this.combinedOptions = (all_tokens[all_tokens.length-1] && this.parameterOptionsToList ? 
          this.parameterOptionsToList.filter((el: any) => {
            return el.compound_name.toLowerCase().includes(all_tokens[all_tokens.length-1].value.toLowerCase()) ||
            ruLayout.toEn(el.compound_name).toLowerCase().includes(all_tokens[all_tokens.length-1].value.toLowerCase())
          }) : [])
        .map((el: any, t: number) => {
          let foundParam = undefined;

          if (this.globalFormulasMode) {
            const that = this
            foundParam = findRecursive(function a(element: any, el_to_find: any): any | undefined {
              if (!element) return undefined

              if ((!that.templateName || element[that.templateName]) && 
              el_to_find.Identity?.ElementId === element.Identity?.ElementId &&
              el_to_find.Identity?.Kind === element.Identity?.Kind &&
              (that.templateName ? el_to_find[that.templateName].Id : el_to_find.Id) === (that.templateName ? element[that.templateName].Id : element.Id) &&
              (!el_to_find.Identity?.Source || (el_to_find.Identity?.Source === element.Identity?.Source))) {
                return [element];
              }
              if (element[that.nestedKeyName]) {
                const childparam = findRecursive(a, el_to_find, element[that.nestedKeyName])
                return childparam ? [ element, ...childparam ] : undefined
              }
              return undefined
            }, el, this.parameterOptions as any[])
          } else {
            foundParam = findParametersByChain(el.IdChain, this.parameterOptions as any[], this.nestedKeyName, this.templateName)
          }

          return {
            label: el.compound_name,
            // these values should not intersect with innerFormulaModel values so that the same element could be added many times
            value: 'param_'+t,
            innerData: el.compound_name,
            apiToken: makeApiFormulaToken(all_tokens[all_tokens.length-1], this.globalFormulasMode ? detailTokenType.GlobalFormula : detailTokenType.CommonParameter, foundParam, this.templateName),
            // indicates that this value should be replaced
            replace: true,
            element: foundParam // IsList inside
          }
        })
      }
      
      if (this.formulaOptions) {
        this.combinedOptions = [ ...this.combinedOptions, ...this.formulaOptions.filter((el: any) => {
          return el.Name.toLowerCase().includes(all_tokens[all_tokens.length-1].value.toLowerCase()) ||
          ruLayout.toEn(el.Name).toLowerCase().includes(all_tokens[all_tokens.length-1].value.toLowerCase())
        }).map((el: any, t: number) => {
          return {
            label: el.Name,
            // these values should not intersect with innerFormulaModel values so that each value could be added many times
            value: 'filter_'+t,
            innerData: ""+el.Name,
            apiToken: makeApiFormulaToken(all_tokens[all_tokens.length-1], detailTokenType.Formula, el),
            // indicates that this value should be replaced
            replace: true,
            element: el
          }
        }) ]
      }
    })

    // abort();
  }

  // prototype function for changing the 'SUM' symbol color and 
  // the ParameterLinkSummablePart.Type field to 
  // ParameterLinkSummablePartType.FirstElement and ParameterLinkSummablePartType.ElementsSum
  // This function will have 2 options:
  // 1) erroneous NOTNUMBER tokens - will open appropriate selector and change token model and internal structure 
  say_hello(t: any) {
    if (!this.innerFormulaModel) { return }

    alert('hello'+t);
    // console.log(this.innerFormulaModel)
    // this.innerFormulaModel[t].sum = !this.innerFormulaModel[t].sum
    // console.log(this.innerFormulaModel)

    // @ts-ignore
    // document.getElementById('hello'+t).style.color = this.innerFormulaModel[t].sum ? 'blue' : 'grey';
  }

  // Changing the ParameterLinkSummablePart.Type field in the model
  toggleSum(ref: string, span_ref: string, parameter_part_id: number) {
    if (!this.innerFormulaModel) { return }

    const modelElement = this.innerFormulaModel.find((el: any) => el.ref_element_id === ref)
    if (modelElement) {
      const token_summablelink = (modelElement.apiToken as AnalyticsFormulaLinkToParameter).ParameterLink.LinkParts.find((part: ParameterLinkSummablePart) => part.Id === parameter_part_id)
      // alert(token_summablelink ? JSON.stringify(token_summablelink) : '');
      if (token_summablelink) {
        token_summablelink.Type = (token_summablelink.Type ? 0 : 1)
        // @ts-ignore
        document.getElementById(span_ref).style.color = token_summablelink.Type ? 'black' : 'grey';
        // console.log(this.innerFormulaModel)
      }
    }
  }

  // Changing the ParameterLinkSummablePart.Type field in the model
  toggleReplaceElement(ref: string, span_ref: string, parameter_part_id: number) {
    if (!this.innerFormulaModel || this.readOnly) { return }

    this.replaceIndex = this.innerFormulaModel.findIndex((el: any) => el.ref_element_id === ref)
    this.selectorOpen = true
  }

  // Changing the ParameterLinkSummablePart.Type field in the model
  toggleInsertElement(val: string, ins_index: number) {
    if (!this.innerFormulaModel || this.readOnly || !this.$refs.formula_field) { return }

    // @ts-ignore
    this.addFormulaToken(val, this.$refs.formula_field.add, ins_index)
  }

  // Changing the ParameterLinkSummablePart.Type field in the model
  toggleRemovePrevious(del_index: number) {
    if (!this.innerFormulaModel || this.readOnly) { return }

    // @ts-ignore
    if (del_index >= 0 && del_index <= this.innerFormulaModel.length - 1) {
      this.innerFormulaModel.splice(del_index, 1);
    }
    this.addFormulaFunc('')
  }

  // on ESC press
  resetFormula() {
    console.log('reset f')
    if (!this.$refs.formula_field || !this.innerFormulaModel || !this.innerFormulaModel.length) { return }

    const f_val = this.innerFormulaModel ? this.innerFormulaModel.map((el: any) => el.innerData).join('') : ''
    this.tempValue = f_val
    this.innerFormulaModel.splice(0)

    // @ts-ignore
    this.$refs.formula_field.updateInputValue(f_val, true)
  }
}
</script>

<style lang="scss">
.q-btn__content > span {
  text-align: left !important;
}

.formula-input, .formula-input > * {
  min-height: 26px !important;
  font-size: 13px;
  margin-left: 6px;

  .q-field__control {
    border-radius: 0 !important;
    min-height: 26px !important;
    font-size: 13px;
  }

  .q-field__control::before {
    border: 0 !important;
    border-bottom: 1px solid $field-border-color !important;
  }

  .q-field__control::after {
    border: 0 !important;
  }

  .q-field__native {
    min-height: 26px !important;
  }

  .q-field__native {
    padding: 0 !important;
  }

  .q-field {
    border-radius: 0 !important;
  }
}
</style>